import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Dialog, DialogHeader, DialogTitle, DialogContent } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { FileText, AlertCircle, Download, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { PdfViewer } from '../../../components/viewers/PdfViewer';
import { PIPEFITTER_FETCH_SEQUENCE_REPORT } from '../../../redux/Pipefitter/types';
import { configurationConstants } from '../constants/configurationConstants';
import { getReportVersions, isReportVersionsFailure, normalizeReportVersionRow } from '../api/reportVersions';
import { PIPEFITTER_REPORT_TYPES } from '../constants/reportVersionTypes';

export type SequenceReportVersionRow = ReturnType<typeof normalizeReportVersionRow>;

export interface SequenceReportViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectNumber: string;
  projectName?: string;
  selectionType: string;
  selectedConfiguration: Record<string, any>;
  canReport?: boolean;
}

function buildSequenceReportQuery(
  projectNumber: string,
  selectionType: string,
  selectedConfiguration: Record<string, any>
): Record<string, string> | null {
  if (!projectNumber) return null;

  let params: Record<string, string> = { projectNumber };

  if (selectionType === configurationConstants.selectionType.spread) {
    params = { ...params, spread: selectedConfiguration?.id?.toString() ?? '' };
  } else if (selectionType === configurationConstants.selectionType.subSpread) {
    params = { ...params, subSpread: selectedConfiguration?.id?.toString() ?? '' };
  } else {
    return null;
  }

  if (!selectedConfiguration?.id) return null;

  return params;
}

function formatUploadDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const SequenceReportViewModal: React.FC<SequenceReportViewModalProps> = ({
  isOpen,
  onClose,
  projectNumber,
  projectName,
  selectionType,
  selectedConfiguration,
  canReport = true,
}) => {
  const dispatch = useDispatch();
  const [viewerFile, setViewerFile] = useState<{ fileUrl: string; fileName: string } | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const lastBlobRef = useRef<Blob | null>(null);

  const [versions, setVersions] = useState<SequenceReportVersionRow[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  const [historyActionError, setHistoryActionError] = useState<string | null>(null);
  const [isOlderExpanded, setIsOlderExpanded] = useState(false);

  const reportFileName = `${projectNumber || 'report'}_Sequence-Report.pdf`;

  const sequenceContextLabel = useMemo(() => {
    if (selectionType === configurationConstants.selectionType.spread) {
      return selectedConfiguration?.spreadName ? String(selectedConfiguration.spreadName) : '';
    }
    if (selectionType === configurationConstants.selectionType.subSpread) {
      return selectedConfiguration?.subSpreadName ? String(selectedConfiguration.subSpreadName) : '';
    }
    return '';
  }, [selectionType, selectedConfiguration]);

  const headerSubtitle = useMemo(() => {
    const parts: string[] = [];
    if (projectName) parts.push(`Project: ${projectName}`);
    if (sequenceContextLabel) {
      const label =
        selectionType === configurationConstants.selectionType.subSpread ? 'Sub-sequence' : 'Sequence';
      parts.push(`${label}: ${sequenceContextLabel}`);
    }
    return parts.join(' · ');
  }, [projectName, sequenceContextLabel, selectionType]);

  const sortedVersions = useMemo(
    () => [...versions].sort((a, b) => b.docRef - a.docRef),
    [versions]
  );

  const latestVersion = sortedVersions[0] ?? null;
  const olderVersions = sortedVersions.length > 1 ? sortedVersions.slice(1) : [];
  const totalVersions = sortedVersions.length;

  const revokeViewerUrl = useCallback(() => {
    setViewerFile((prev) => {
      if (prev?.fileUrl?.startsWith('blob:')) {
        window.URL.revokeObjectURL(prev.fileUrl);
      }
      return null;
    });
  }, []);

  const scopeQuery = useMemo(
    () => buildSequenceReportQuery(projectNumber, selectionType, selectedConfiguration),
    [projectNumber, selectionType, selectedConfiguration]
  );

  const loadVersions = useCallback(async () => {
    if (!scopeQuery) {
      setVersions([]);
      return;
    }
    setVersionsLoading(true);
    setVersionsError(null);
    try {
      const envelope = await getReportVersions({
        projectNumber: scopeQuery.projectNumber,
        reportType: PIPEFITTER_REPORT_TYPES.SEQUENCE,
        spread: scopeQuery.spread,
        subSpread: scopeQuery.subSpread,
      });
      if (isReportVersionsFailure(envelope)) {
        setVersions([]);
        setVersionsError(envelope.message || 'Could not load version history.');
        return;
      }
      const rawList = envelope.data?.versions;
      const list = Array.isArray(rawList) ? rawList.map(normalizeReportVersionRow).filter((r) => r.fileUrl) : [];
      setVersions(list);
    } catch (e: any) {
      const body = e?.response?.data;
      const parsed = typeof body === 'object' ? body : { message: undefined };
      const msg =
        parsed?.message ||
        (typeof body === 'object' && body?.message) ||
        e?.message ||
        'Could not load version history.';
      setVersions([]);
      setVersionsError(typeof msg === 'string' ? msg : 'Could not load version history.');
    } finally {
      setVersionsLoading(false);
    }
  }, [scopeQuery]);

  useEffect(() => {
    if (!isOpen) {
      revokeViewerUrl();
      setGenerateError(null);
      setIsGenerating(false);
      lastBlobRef.current = null;
      setVersions([]);
      setVersionsError(null);
      setHistoryActionError(null);
      setIsOlderExpanded(false);
      return;
    }
    if (scopeQuery) {
      void loadVersions();
    }
  }, [isOpen, scopeQuery, loadVersions, revokeViewerUrl]);

  const fetchReportBlob = useCallback((): Promise<Blob | null> => {
    const basePayload = scopeQuery;
    if (!basePayload || !canReport) {
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      dispatch({
        type: PIPEFITTER_FETCH_SEQUENCE_REPORT,
        payload: { ...basePayload, skipSequenceTableLoading: true },
        cb: (blob: Blob) => {
          lastBlobRef.current = blob;
          resolve(blob);
        },
        error: () => {
          resolve(null);
        },
      });
    });
  }, [dispatch, scopeQuery, canReport]);

  const handleGenerateReport = async () => {
    setGenerateError(null);
    let blob = lastBlobRef.current;
    if (!blob) {
      setIsGenerating(true);
      try {
        blob = await fetchReportBlob();
      } finally {
        setIsGenerating(false);
      }
    }
    if (!blob) {
      setGenerateError('Could not generate sequence report.');
      return;
    }
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = reportFileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    void loadVersions();
  };

  const openReportInViewer = (row: SequenceReportVersionRow) => {
    setHistoryActionError(null);
    if (!row.fileUrl) {
      setHistoryActionError('This version has no file URL.');
      return;
    }
    setViewerFile({ fileUrl: row.fileUrl, fileName: row.fileName || 'Sequence-Report.pdf' });
  };

  const closeViewer = () => {
    revokeViewerUrl();
  };

  const canRequestReport = canReport && Boolean(scopeQuery);

  const renderDownloadButton = (row: SequenceReportVersionRow) => (
    <Button
      variant="outline"
      size="sm"
      onClick={() => openReportInViewer(row)}
      className="flex h-8 items-center gap-1.5 border-[#E2E5EA] px-3 text-[12px] text-text-grey hover:bg-page-bg"
      disabled={!row.fileUrl}
    >
      <Download className="h-3.5 w-3.5" />
      Download
    </Button>
  );

  if (viewerFile) {
    return (
      <PdfViewer fileUrl={viewerFile.fileUrl} fileName={viewerFile.fileName} onClose={closeViewer} />
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[90vh] w-[95vw] max-w-[1100px] flex-col gap-0 overflow-hidden bg-page-bg p-0">
        <DialogHeader className="flex flex-shrink-0 flex-row items-center justify-between gap-4 border-b border-[#E2E5EA] bg-white py-4 pl-4 pr-14 sm:pl-6">
          <div className="min-w-0">
            <DialogTitle className="text-[18px] font-semibold leading-tight text-blueprint-blue">
              Sequence Reports
            </DialogTitle>
            {headerSubtitle ? (
              <p className="mt-1 truncate text-[12px] text-heritage-grey">{headerSubtitle}</p>
            ) : null}
          </div>
          {canRequestReport ? (
            <Button
              type="button"
              size="sm"
              onClick={() => void handleGenerateReport()}
              disabled={isGenerating}
              loading={isGenerating}
              loadingText="Generating…"
              className="flex-shrink-0"
            >
              <FileText className="mr-1.5 h-4 w-4" />
              Generate Report
            </Button>
          ) : null}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {!canRequestReport ? (
            <div className="flex items-center gap-2 rounded-lg border border-[#E2E5EA] bg-white p-4">
              <FileText className="h-5 w-5 flex-shrink-0 text-heritage-grey" />
              <span className="text-[13px] text-heritage-grey">
                Select a sequence with a valid spread or sub-sequence to view reports.
              </span>
            </div>
          ) : (
            <>
              {generateError ? (
                <div className="mb-4 flex items-center gap-2 rounded-lg border border-[rgba(235,28,55,0.2)] bg-[rgba(235,28,55,0.06)] p-3">
                  <AlertCircle className="h-5 w-5 flex-shrink-0 text-hero-red" />
                  <span className="text-[13px] text-hero-red">{generateError}</span>
                </div>
              ) : null}

              {historyActionError ? (
                <div className="mb-4 flex items-center gap-2 rounded-lg border border-[rgba(237,139,0,0.25)] bg-[rgba(237,139,0,0.08)] p-3 text-[13px] text-text-grey">
                  {historyActionError}
                </div>
              ) : null}

              {versionsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="mr-2 h-5 w-5 animate-spin text-heritage-grey" />
                  <span className="text-[13px] text-heritage-grey">Loading previous reports…</span>
                </div>
              ) : versionsError ? (
                <div className="flex items-center gap-2 rounded-lg border border-[rgba(235,28,55,0.2)] bg-[rgba(235,28,55,0.06)] p-3">
                  <AlertCircle className="h-5 w-5 flex-shrink-0 text-hero-red" />
                  <span className="text-[13px] text-hero-red">{versionsError}</span>
                </div>
              ) : totalVersions === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-xl border border-[#E2E5EA] bg-white py-16">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-page-bg">
                    <FileText className="h-6 w-6 text-heritage-grey" />
                  </div>
                  <h3 className="mb-1 font-heading text-[14px] font-semibold text-blueprint-blue">No Reports Yet</h3>
                  <p className="max-w-xs text-center text-[12px] text-heritage-grey">
                    No sequence reports have been generated for this scope yet. Use Generate Report to create one.
                  </p>
                </div>
              ) : latestVersion ? (
                <div className="overflow-hidden rounded-xl border border-[#E2E5EA] bg-white">
                  <div className="flex items-center justify-between border-b border-[#E2E5EA] bg-page-bg px-4 py-2.5">
                    <h4 className="text-[11px] font-semibold uppercase tracking-wider text-heritage-grey">
                      Sequence Report
                    </h4>
                    <span className="text-[11px] text-heritage-grey">
                      {totalVersions} {totalVersions === 1 ? 'version' : 'versions'}
                    </span>
                  </div>

                  <div className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-page-bg">
                    <div className="flex min-w-[110px] flex-shrink-0 items-center gap-2">
                      {latestVersion.docRef ? (
                        <span className="rounded-full bg-[rgba(0,39,63,0.06)] px-2 py-0.5 text-[11px] font-semibold text-blueprint-blue">
                          v{latestVersion.docRef}
                        </span>
                      ) : null}
                      <span className="rounded-full bg-[rgba(31,154,58,0.08)] px-2 py-0.5 text-[11px] font-semibold text-galvanizing-green">
                        Latest
                      </span>
                    </div>
                    <div className="min-w-0 flex-1 text-[12px] text-text-grey">
                      {formatUploadDate(latestVersion.uploadDate)}
                    </div>
                    <div className="flex-shrink-0">{renderDownloadButton(latestVersion)}</div>
                  </div>

                  {olderVersions.length > 0 ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setIsOlderExpanded((v) => !v)}
                        className="flex w-full items-center gap-1.5 border-t border-[#E2E5EA] px-4 py-2 text-[12px] text-heritage-grey transition-colors hover:bg-page-bg"
                      >
                        {isOlderExpanded ? (
                          <ChevronUp className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5" />
                        )}
                        <span>
                          {isOlderExpanded ? 'Hide' : 'Show'} {olderVersions.length} older{' '}
                          {olderVersions.length === 1 ? 'version' : 'versions'}
                        </span>
                      </button>
                      {isOlderExpanded ? (
                        <div className="border-t border-[#E2E5EA]">
                          {olderVersions.map((row, index) => (
                            <div
                              key={row.docId || `${row.docRef}-${row.fileUrl}`}
                              className="flex items-center gap-3 border-t border-[#EEF0F3] px-4 py-2.5 transition-colors first:border-t-0 hover:bg-page-bg"
                            >
                              <div className="flex min-w-[110px] flex-shrink-0 items-center">
                                {row.docRef ? (
                                  <span className="rounded-full bg-[#EEF0F3] px-2 py-0.5 text-[11px] font-medium text-heritage-grey">
                                    v{row.docRef}
                                  </span>
                                ) : null}
                              </div>
                              <div className="min-w-0 flex-1 text-[12px] text-heritage-grey">
                                {formatUploadDate(row.uploadDate)}
                              </div>
                              <div className="flex-shrink-0">{renderDownloadButton(row)}</div>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
